const CHARGEBEE_SCRIPT_SRC = "https://js.chargebee.com/v2/chargebee.js";
const CHARGEBEE_SCRIPT_TIMEOUT_MS = 10000;

let chargebeeScriptPromise: Promise<ChargebeeGlobal> | null = null;

function getExistingChargebeeScript(): HTMLScriptElement | null {
  return document.querySelector(
    `script[src="${CHARGEBEE_SCRIPT_SRC}"]`
  ) as HTMLScriptElement | null;
}

function resolveChargebeeGlobal(): ChargebeeGlobal | null {
  return window.Chargebee ?? null;
}

export function loadChargebeeScript(): Promise<ChargebeeGlobal> {
  const existingGlobal = resolveChargebeeGlobal();
  if (existingGlobal) {
    return Promise.resolve(existingGlobal);
  }

  if (chargebeeScriptPromise) {
    return chargebeeScriptPromise;
  }

  chargebeeScriptPromise = new Promise<ChargebeeGlobal>((resolve, reject) => {
    const existingScript = getExistingChargebeeScript();
    const script = existingScript ?? document.createElement("script");
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("Timed out loading Chargebee library"));
    }, CHARGEBEE_SCRIPT_TIMEOUT_MS);

    const cleanup = () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
      window.clearTimeout(timeoutId);
      chargebeeScriptPromise = null;
    };

    const handleLoad = () => {
      const chargebee = resolveChargebeeGlobal();
      cleanup();

      if (!chargebee) {
        reject(new Error("Chargebee library loaded without a global"));
        return;
      }

      resolve(chargebee);
    };

    const handleError = () => {
      cleanup();
      reject(new Error("Failed to load Chargebee library"));
    };

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);

    if (!existingScript) {
      script.src = CHARGEBEE_SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return chargebeeScriptPromise;
}
